
// --- Vue & Template imports ---
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

// Models
import { MenuItem, Menu } from '@/models/Menu.model';

// --- Third Party imports ---
import gsap from 'gsap';
import colors from 'vuetify/lib/util/colors';

@Component
export default class SideDrawer extends Vue {
  @Prop(HTMLDivElement) readonly parentElement!: HTMLDivElement;
  @Prop(String) readonly drawerLogo!: string;
  @Prop(String) readonly drawerLogoMobile!: string;
  @Prop(String) readonly openSideMenuIcon!: string;
  @Prop(String) readonly closeSideMenuIcon!: string;
  @Prop(String) readonly openSideMenuIconMobile!: string;
  @Prop(String) readonly closeSideMenuIconMobile!: string;
  @Prop(Boolean) readonly defaultOpen!: boolean;
  @Prop(Boolean) readonly allowToggle!: boolean;
  @Prop(Object) readonly menuOptions!: Menu;
  @Prop(Boolean) readonly mobileOnly!: boolean;

  sidedrawerOpened: boolean = false;
  localStorageOpen: boolean = false;
  debounceTimeout: number = 0;

  refs!: {
    sideDrawerContent: HTMLDivElement,
  };

  @Watch('$route.path')
  onMobileOnlyChanged() {
    this.mountedActions();
  }


  mounted() {
   this.mountedActions();
  }

  mountedActions () {
    if (this.defaultOpen || this.allowToggle === false) {
        if (this.mobileOnly) {
            this.closeDrawer();
        } else {
            this.openDrawer();
        }   
    } else {
      this.closeDrawer();
    }

    this.$root.$on('toggleSideDrawer', this.toggleSideDrawer);
  }

  toggleSideDrawer() {
    if (this.sidedrawerOpened) {
      this.closeDrawer();
    } else {
      this.openDrawer();      
    }
  }

  sideDrawerResized() {
    if (this.mobileOnly) {
      return;
    }

    this.$nextTick(() => {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      this.debounceTimeout = setTimeout(() => {
        if (this.sidedrawerOpened) {
          if (this.$vuetify.breakpoint.lgAndUp) {
            gsap.set(this.parentElement, { paddingLeft: 300 });
            this.$root.$emit('setSideDrawerState', this.sidedrawerOpened);
          } else {
            if (this.$vuetify.breakpoint.mdAndDown && this.defaultOpen) {
              gsap.set(this.parentElement, { paddingLeft: 100 });
              this.sidedrawerOpened = true;
              this.$root.$emit('setSideDrawerState', this.sidedrawerOpened);
            } else {
              this.closeDrawer();
            }
          }
        } else {
          if (this.$vuetify.breakpoint.lgAndUp) {
            this.openDrawer();
          } else {
            if (this.$vuetify.breakpoint.mdAndDown && this.defaultOpen) {
              this.openDrawer();
            } else {
              this.closeDrawer();
            }
          }
        }
      }, 500);
    }); 
  }

  openDrawer() {
    gsap.to(this.parentElement, {
      duration: 0.1,
      paddingLeft: this.$vuetify.breakpoint.lgAndUp ? (this.mobileOnly ? 0 : 300) : 0,
      onStart: () => {
        this.sidedrawerOpened = true;
        this.$root.$emit('setSideDrawerState', this.sidedrawerOpened);
        localStorage.drawerOpen = true;
        gsap.to(this.$el, {
          duration: 0.1,
          width: 300,
          left: 0, 
        });
        if (this.$vuetify.breakpoint.mdAndDown) {
          gsap.set(document.body, {position: 'fixed', left: 0, right: 0});
        }
      },
    });
  }

  closeDrawer() {
    gsap.to(this.parentElement, {
      duration: 0.1,
      paddingLeft: this.$vuetify.breakpoint.lgAndUp ? (this.mobileOnly ? 0 : 100) : 0,
      onStart: () => {
        this.sidedrawerOpened = false;
        this.$root.$emit('setSideDrawerState', this.sidedrawerOpened);
        localStorage.drawerOpen = false;
        gsap.to(this.$el, {
          duration: 0.1,
          width: this.$vuetify.breakpoint.lgAndUp ? (this.mobileOnly ? 300 : 100) : 300,
          left: this.$vuetify.breakpoint.lgAndUp ? (this.mobileOnly ? -300 : 0) : -300, 
        });
        gsap.set(document.body, {position: 'relative', left: 'auto', right: 'auto'});
      },
    });
  }

  menuItemClicked(event: Event, clickedMenuItem: MenuItem) {
    if (clickedMenuItem.customEvent) {
      this.$root.$emit(clickedMenuItem.customEvent);
      return; // ??? should this prevent routes etc ???
    }

    if (clickedMenuItem.routeName) {
      this.redirect(clickedMenuItem.routeName);
    }

    if (clickedMenuItem.children && clickedMenuItem.childrenToggle) {
      if (event.target) {
        const item = event.currentTarget  as HTMLElement;
        const sibling = item.nextElementSibling;

        if (sibling) {
          if (item.classList.contains('state--open')) {
            gsap.to(sibling, {
              duration: 0.1,
              height: 0,
              onStart: () => {
                item.classList.remove('state--open');
              },
            });            
          } else {
            gsap.set(sibling, { height: 'auto' });
            gsap.from(sibling, {
              duration: 0.1,
              height: 0,
              onStart: () => {
                item.classList.add('state--open');
              },
            });
          }
        }
      }
    }
  }

  getTheme() {
    if (this.menuOptions && this.menuOptions.theme) {
      let theme = {};

      for (const [colorKey, color] of Object.entries(this.menuOptions.theme)) {
        theme = {...theme, ...{['--' + colorKey]: this.getColor(color + '')}};
      }

      return theme;
    }
    return {};
  }

  isMenuChildrenInRoute(menuItem: MenuItem) {
    // check if any of the children actually matches the route, if they don't then apply defaults
    let childMatchesRoute = false;

    if (menuItem.children) {
      for (const [itemIndex, item] of menuItem.children.entries()) {
        if (item.routeName === this.$route.name) {
          childMatchesRoute = true;
        }
      }
    }

    if (childMatchesRoute) {
      return true;
    }

    return false;
  }

  getColor(colorString: string) {
    if (colorString.indexOf('#') > -1) {
      return colorString;
    }

    if (this.$vuetify.theme.currentTheme) {
      for (const [colorIndex, color] of Object.entries(this.$vuetify.theme.currentTheme)) {
        if (colorString === colorIndex) {
          return color + '';
        }
      }
    }

    if (colors) {
      for (const [colorIndex, color] of Object.entries(colors)) {
        if (colorString === colorIndex) {
          return color.base + '';
        }
      }
    }

    return colorString;
  }

  redirect(route: string) {
    // check if current route is the route that the menu is going to, ignore if it is
    if (this.$route.name === route) {
      return;
    }

    this.$router.push({ name: route});
  }
}
